/**
 * Geocoding utility for converting addresses to coordinates
 */

// Cache for geocoded addresses to avoid repeated API calls
const geocodeCache = new Map();
const geocodeWarnedAddresses = new Set();

/**
 * Geocode an address using Google Maps Geocoding API
 * @param {string} address - The full address to geocode
 * @returns {Promise<{lat: number, lng: number} | null>}
 */
export const geocodeAddress = async (address) => {
  if (!address) return null;
  
  // Check cache first
  if (geocodeCache.has(address)) {
    return geocodeCache.get(address);
  }

  try {
    // Using Google Maps Geocoding API (requires API key)
    // For production, you should store the API key in environment variables
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey) {
      // Fallback: Use approximate coordinates based on city
      return getFallbackCoordinates(address);
    }

    const encodedAddress = encodeURIComponent(address);
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${apiKey}`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.status === 'OK' && data.results.length > 0) {
      const location = data.results[0].geometry.location;
      const coords = { lat: location.lat, lng: location.lng };
      
      // Cache the result
      geocodeCache.set(address, coords);
      
      return coords;
    }
    
    if (!geocodeWarnedAddresses.has(address)) {
      geocodeWarnedAddresses.add(address);
      if (data.status !== 'REQUEST_DENIED') {
      }
    }
    const fallback = getFallbackCoordinates(address, { quiet: true });
    geocodeCache.set(address, fallback);
    return fallback;
  } catch (error) {
    return getFallbackCoordinates(address);
  }
};

/**
 * Fallback coordinates for common cities (when API is not available)
 */
const cityCoordinates = {
  // North America - USA
  'new york': { lat: 40.7128, lng: -74.0060 },
  'los angeles': { lat: 34.0522, lng: -118.2437 },
  'chicago': { lat: 41.8781, lng: -87.6298 },
  'san francisco': { lat: 37.7749, lng: -122.4194 },
  'austin': { lat: 30.2672, lng: -97.7431 },
  'seattle': { lat: 47.6062, lng: -122.3321 },
  'boston': { lat: 42.3601, lng: -71.0589 },
  'denver': { lat: 39.7392, lng: -104.9903 },
  'miami': { lat: 25.7617, lng: -80.1918 },
  'atlanta': { lat: 33.7490, lng: -84.3880 },
  'fresno': { lat: 36.7378, lng: -119.7871 },
  
  // North America - Canada
  'toronto': { lat: 43.6532, lng: -79.3832 },
  'vancouver': { lat: 49.2827, lng: -123.1207 },
  'montreal': { lat: 45.5017, lng: -73.5673 },
  'calgary': { lat: 51.0447, lng: -114.0719 },
  'ottawa': { lat: 45.4215, lng: -75.6972 },
  
  // Europe
  'london': { lat: 51.5074, lng: -0.1278 },
  'paris': { lat: 48.8566, lng: 2.3522 },
  'berlin': { lat: 52.5200, lng: 13.4050 },
  'amsterdam': { lat: 52.3676, lng: 4.9041 },
  'madrid': { lat: 40.4168, lng: -3.7038 },
  'rome': { lat: 41.9028, lng: 12.4964 },
  'barcelona': { lat: 41.3851, lng: 2.1734 },
  'munich': { lat: 48.1351, lng: 11.5820 },
  
  // Asia
  'tokyo': { lat: 35.6762, lng: 139.6503 },
  'shanghai': { lat: 31.2304, lng: 121.4737 },
  'beijing': { lat: 39.9042, lng: 116.4074 },
  'singapore': { lat: 1.3521, lng: 103.8198 },
  'hong kong': { lat: 22.3193, lng: 114.1694 },
  'seoul': { lat: 37.5665, lng: 126.9780 },
  'bangalore': { lat: 12.9716, lng: 77.5946 },
  'mumbai': { lat: 19.0760, lng: 72.8777 },
  'delhi': { lat: 28.7041, lng: 77.1025 },
  
  // Other
  'sydney': { lat: -33.8688, lng: 151.2093 },
  'melbourne': { lat: -37.8136, lng: 144.9631 },
  'dubai': { lat: 25.2048, lng: 55.2708 },
  'sao paulo': { lat: -23.5505, lng: -46.6333 },
  'mexico city': { lat: 19.4326, lng: -99.1332 },
};

/**
 * Get fallback coordinates based on city name in address
 */
const getFallbackCoordinates = (address, options = {}) => {
  const { quiet = false } = options;
  const lowerAddress = address.toLowerCase();

  for (const [city, coords] of Object.entries(cityCoordinates)) {
    if (lowerAddress.includes(city)) {
      return coords;
    }
  }

  const countryCoords = extractCountryCoordinates(lowerAddress);
  if (countryCoords) {
    return countryCoords;
  }

  if (!quiet && !geocodeWarnedAddresses.has(`fallback:${address}`)) {
    geocodeWarnedAddresses.add(`fallback:${address}`);
  }
  return { lat: 39.8283, lng: -98.5795 };
};

/**
 * Extract country-level coordinates from address string
 */
const extractCountryCoordinates = (lowerAddress) => {
  const countryMappings = {
    'usa': { lat: 37.0902, lng: -95.7129 },
    'united states': { lat: 37.0902, lng: -95.7129 },
    'canada': { lat: 56.1304, lng: -106.3468 },
    'mexico': { lat: 23.6345, lng: -102.5528 },
    'uk': { lat: 55.3781, lng: -3.4360 },
    'united kingdom': { lat: 55.3781, lng: -3.4360 },
    'france': { lat: 46.2276, lng: 2.2137 },
    'germany': { lat: 51.1657, lng: 10.4515 },
    'china': { lat: 35.8617, lng: 104.1954 },
    'japan': { lat: 36.2048, lng: 138.2529 },
    'india': { lat: 20.5937, lng: 78.9629 },
    'australia': { lat: -25.2744, lng: 133.7751 },
    'brazil': { lat: -14.2350, lng: -51.9253 }
  };
  
  for (const [country, coords] of Object.entries(countryMappings)) {
    if (lowerAddress.includes(country)) {
      return coords;
    }
  }
  
  return null;
};

/**
 * Create a Google Maps URL for a route between two addresses
 * @param {string} startAddress - Starting address
 * @param {string} endAddress - Ending address
 * @returns {string} Google Maps URL
 */
export const getGoogleMapsRouteUrl = (startAddress, endAddress) => {
  const origin = encodeURIComponent(startAddress);
  const destination = encodeURIComponent(endAddress);
  
  return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;
};

/**
 * Create a Google Maps embed URL for displaying a route
 * @param {string} startAddress - Starting address
 * @param {string} endAddress - Ending address
 * @returns {string} Google Maps embed URL
 */
export const getGoogleMapsEmbedUrl = (startAddress, endAddress) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const origin = encodeURIComponent(startAddress);
  const destination = encodeURIComponent(endAddress);
  return `https://www.google.com/maps/embed/v1/directions?key=${apiKey}&origin=${origin}&destination=${destination}`;
};

/**
 * Batch geocode multiple addresses
 * @param {string[]} addresses - Array of addresses to geocode
 * @returns {Promise<Map<string, {lat: number, lng: number}>>}
 */
export const batchGeocodeAddresses = async (addresses) => {
  const results = new Map();
  
  // Process in batches to avoid rate limiting
  const batchSize = 5;
  for (let i = 0; i < addresses.length; i += batchSize) {
    const batch = addresses.slice(i, i + batchSize);
    const promises = batch.map(async (address) => {
      const coords = await geocodeAddress(address);
      return { address, coords };
    });
    
    const batchResults = await Promise.all(promises);
    batchResults.forEach(({ address, coords }) => {
      if (coords) {
        results.set(address, coords);
      }
    });
    
    // Add a small delay between batches
    if (i + batchSize < addresses.length) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  }
  
  return results;
};

/**
 * Get coordinates for an address object (supports both UserAddressDTO from watchlist)
 * Falls back to country-level coordinates if specific address geocoding fails
 * @param {Object} address - Address object with country, city, etc.
 * @returns {Promise<{lat: number, lng: number} | null>}
 */
export const getCoordinatesForAddress = async (address) => {
  if (!address) return null;
  
  // If the address already has coordinates, use them
  if (address.lat && address.lng) {
    return { lat: address.lat, lng: address.lng };
  }
  
  // Try to geocode the full address if available
  if (address.getFullAddress) {
    const fullAddress = address.getFullAddress();
    const coords = await geocodeAddress(fullAddress);
    if (coords && coords.lat !== 0 && coords.lng !== 0) {
      return coords;
    }
  }
  // Build from camelCase or snake_case (API)
  const street = address.streetAddress ?? address.street_address ?? '';
  const city = address.city ?? '';
  const state = address.stateProvince ?? address.state_province ?? '';
  const postal = address.postalCode ?? address.postal_code ?? '';
  const country = address.country ?? '';
  if (street || city || state || postal || country) {
    const parts = [street, city, state, postal, country].filter(Boolean);
    if (parts.length > 0) {
      const fullAddress = parts.join(', ');
      const coords = await geocodeAddress(fullAddress);
      if (coords && coords.lat !== 0 && coords.lng !== 0) {
        return coords;
      }
    }
  }
  // Fallback to country-level coordinates
  if (country) {
    return getCountryCoordinates(country);
  }
  return null;
};

/**
 * Get country coordinates from COUNTRY_COORDINATES mapping.
 * Supports common names and codes so watchlist addresses resolve correctly for map markers.
 * @param {string} country - Country name or code
 * @returns {{lat: number, lng: number}}
 */
export const getCountryCoordinates = (country) => {
  if (!country || typeof country !== 'string') return { lat: 0, lng: 0 };
  const countryCoords = {
    'Argentina': { lat: -38.4161, lng: -63.6167 },
    'Australia': { lat: -25.2744, lng: 133.7751 },
    'Brazil': { lat: -14.2350, lng: -51.9253 },
    'Canada': { lat: 56.1304, lng: -106.3468 },
    'China': { lat: 35.8617, lng: 104.1954 },
    'France': { lat: 46.2276, lng: 2.2137 },
    'Germany': { lat: 51.1657, lng: 10.4515 },
    'India': { lat: 20.5937, lng: 78.9629 },
    'Indonesia': { lat: -0.7893, lng: 113.9213 },
    'Italy': { lat: 41.8719, lng: 12.5674 },
    'Japan': { lat: 36.2048, lng: 138.2529 },
    'Mexico': { lat: 23.6345, lng: -102.5528 },
    'Netherlands': { lat: 52.1326, lng: 5.2913 },
    'Russia': { lat: 61.5240, lng: 105.3188 },
    'Singapore': { lat: 1.3521, lng: 103.8198 },
    'South Africa': { lat: -30.5595, lng: 22.9375 },
    'South Korea': { lat: 35.9078, lng: 127.7669 },
    'Spain': { lat: 40.4637, lng: -3.7492 },
    'United Kingdom': { lat: 55.3781, lng: -3.4360 },
    'United States': { lat: 37.0902, lng: -95.7129 },
    'United States of America': { lat: 37.0902, lng: -95.7129 },
    'USA': { lat: 37.0902, lng: -95.7129 },
    'UK': { lat: 55.3781, lng: -3.4360 },
    'Ireland': { lat: 53.1424, lng: -7.6921 },
    'Switzerland': { lat: 46.8182, lng: 8.2275 },
    'Belgium': { lat: 50.5039, lng: 4.4699 },
    'Poland': { lat: 51.9194, lng: 19.1451 },
    'Taiwan': { lat: 23.6978, lng: 120.9605 },
    'Vietnam': { lat: 14.0583, lng: 108.2772 },
    'Thailand': { lat: 15.8700, lng: 100.9925 },
    'Malaysia': { lat: 4.2105, lng: 101.9758 },
    'Philippines': { lat: 12.8797, lng: 121.7740 },
    'Turkey': { lat: 38.9637, lng: 35.2433 },
    'Saudi Arabia': { lat: 23.8859, lng: 45.0792 },
    'Israel': { lat: 31.0461, lng: 34.8516 },
    'Egypt': { lat: 26.8206, lng: 30.8025 },
    'Nigeria': { lat: 9.0820, lng: 8.6753 },
    'Kenya': { lat: -0.0236, lng: 37.9062 },
    'Colombia': { lat: 4.5709, lng: -74.2973 },
    'Chile': { lat: -35.6751, lng: -71.5430 },
    'Peru': { lat: -9.1900, lng: -75.0152 },
  };
  const key = country.trim();
  if (countryCoords[key]) return countryCoords[key];
  const lower = key.toLowerCase();
  const byLower = Object.fromEntries(
    Object.entries(countryCoords).map(([k, v]) => [k.toLowerCase(), v])
  );
  if (byLower[lower]) return byLower[lower];
  if (lower === 'us' || lower === 'u.s.' || lower === 'u.s.a.') return countryCoords['USA'];
  if (lower === 'u.k.' || lower === 'gb' || lower === 'great britain') return countryCoords['UK'];
  return { lat: 0, lng: 0 };
};

